"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Popup = exports.DEFAULT_OVERLAY_CONTENT = void 0;
const window_blocked_error_1 = require("./error/window-blocked-error");
const index_1 = __importDefault(require("../utils/overlay/index"));
const windowPolling_1 = require("./windowPolling");
const viewportConfig_js_1 = __importDefault(require("../utils/viewportConfig.js"));
exports.DEFAULT_OVERLAY_CONTENT = {
    text: "Don't see the Klarna window? We'll help you open it to complete your purchase.",
    buttonLabel: 'Continue',
};
const { maxAppHeight, preferredAppWidth } = viewportConfig_js_1.default.viewport;
const getCenterPositions = (popupWidth, popupHeight) => {
    const width = screen.width;
    const height = screen.height;
    // Fixes dual-screen position
    // window.screenLeft/Top: Most browsers
    // window.screenX/Y: Firefox
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
    const left = (width - popupWidth) / 2 + dualScreenLeft;
    const top = (height - popupHeight) / 2 + dualScreenTop;
    return { top, left };
};
class Popup {
    constructor(fullscreenWindow = window) {
        this.keepOverlayOpen = false;
        this.openOverlay = (onOverlayClick, { overlayContent, previousActiveElement, onOverlayClose }) => {
            const overlay = new index_1.default({
                windowOpener: this.windowOpener,
                overlayContent,
                onOverlayClick,
                onOverlayClose,
                previousActiveElement,
            });
            overlay.create();
        };
        this.openWindow = (url, { id = 'Klarna', hideOverlay = false, keepOverlayOpen = false, overlayContent, previousActiveElement, onOverlayClose = () => { }, onOverlayClick, onPopupClose = () => { }, onPopupReferenceLost = () => { }, styles: { width = preferredAppWidth, height = maxAppHeight } = {}, }) => {
            this.keepOverlayOpen = keepOverlayOpen;
            const onOverlayClickDefaultCallback = () => {
                if (this.keepOverlayOpen &&
                    (!this.openedWindow || this.openedWindow?.closed)) {
                    const urlToReopen = index_1.default.ACTIVE_OVERLAY?.getLastPopupUrl() || url;
                    const { top, left } = getCenterPositions(width, height);
                    const reopenedWindow = window.open(urlToReopen, id, `resizable=yes, width=${width}, height=${height}, top=${top}, left=${left}`);
                    if (!reopenedWindow) {
                        // This error can't be caught by the integrator, display error message?
                        // In order for the end-user to end up here, the end user would have to have popup blockers enabled after closing the first popup
                        console.error('Popup window was blocked from re-opening');
                        throw new window_blocked_error_1.WindowBlockedError('Popup window was blocked from re-opening');
                    }
                    this.openedWindow = reopenedWindow;
                    index_1.default.ACTIVE_OVERLAY?.connectPopup(this.openedWindow);
                    if (urlToReopen === 'about:blank') {
                        this.injectKlarnaHeartbeat();
                    }
                }
                this.openedWindow?.focus();
            };
            // If a new popup is opened with hideOverlay === true, hide the overlay
            if (index_1.default.ACTIVE_OVERLAY && hideOverlay) {
                index_1.default.ACTIVE_OVERLAY.hide();
            }
            // If a window is open, change the url of the window instead of opening a new one
            if (this.openedWindow && !this.openedWindow.closed) {
                this.updateWindowURL(url);
                this.openedWindow.focus();
            }
            else {
                const { top, left } = getCenterPositions(width, height);
                const openedWindow = window.open(url, id, `resizable=yes, width=${width}, height=${height}, top=${top}, left=${left}`);
                if (!openedWindow) {
                    throw new window_blocked_error_1.WindowBlockedError('Popup window was blocked from opening');
                }
                this.openedWindow = openedWindow;
                if (url === 'about:blank') {
                    this.injectKlarnaHeartbeat();
                }
                this.addUnloadListener();
            }
            if (!hideOverlay) {
                // Make sure there is always an Overlay unless integrator wants to hide it
                // The default behavior for onOverlayClick when keepOverlayOpen is to try to reopen the popup with the latest url specified
                if (!index_1.default.ACTIVE_OVERLAY) {
                    const overlay = new index_1.default({
                        windowOpener: this.windowOpener,
                        overlayContent,
                        popupUrl: url,
                        onOverlayClick: onOverlayClick
                            ? onOverlayClick
                            : onOverlayClickDefaultCallback,
                        onOverlayClose,
                        previousActiveElement,
                    });
                    overlay.create();
                }
                else {
                    // Update the overlay with desired content and callbacks
                    index_1.default.ACTIVE_OVERLAY.update({
                        windowOpener: this.windowOpener,
                        overlayContent,
                        popupUrl: url,
                        onOverlayClick: onOverlayClick
                            ? onOverlayClick
                            : onOverlayClickDefaultCallback,
                        onOverlayClose,
                        previousActiveElement,
                    });
                }
                // The overlay instance keeps track of the popups to close them when the overlay is closed
                index_1.default.ACTIVE_OVERLAY?.connectPopup(this.openedWindow);
            }
            // trigger onPopupClose when the popup is closed
            // and close the overlay if it's not kept open
            const windowPoll = new windowPolling_1.WindowPoll(this.openedWindow, () => {
                onPopupClose();
                if (!this.keepOverlayOpen) {
                    index_1.default.ACTIVE_OVERLAY?.destroy();
                }
                this.removeUnloadListener();
            });
            windowPoll.start();
            const intervalId = setInterval(() => {
                if (!this.openedWindow) {
                    clearInterval(intervalId);
                    onPopupReferenceLost();
                    this.removeUnloadListener();
                }
            }, 1000);
        };
        this.closeOpenedWindow = () => {
            if (!this.openedWindow?.closed) {
                this.openedWindow?.close();
                if (!this.keepOverlayOpen) {
                    this.removeUnloadListener();
                    index_1.default.ACTIVE_OVERLAY?.destroy();
                }
            }
        };
        this.getOpenedWindow = () => {
            return this.openedWindow || null;
        };
        this.updateWindowURL = (newUrl) => {
            if (this.openedWindow && !this.openedWindow.closed) {
                index_1.default.ACTIVE_OVERLAY?.updateLastPopupUrl(newUrl);
                this.openedWindow.location.href = newUrl;
                if (newUrl === 'about:blank' || newUrl === '') {
                    this.injectKlarnaHeartbeat();
                }
            }
            else {
                throw new Error('Popup is closed');
            }
        };
        this.windowOpener = fullscreenWindow;
    }
    static getInstance(windowOpener) {
        if (!Popup.instance) {
            Popup.instance = new Popup(windowOpener);
        }
        return Popup.instance;
    }
    addUnloadListener() {
        this.windowOpener.addEventListener('unload', this.closeOpenedWindow);
    }
    removeUnloadListener() {
        this.windowOpener.removeEventListener('unload', this.closeOpenedWindow);
    }
    injectKlarnaHeartbeat() {
        const klarnaLogoHtmlAndStyle = `
        <style>
          @keyframes heartbeat {
            0%,
            20%,
            50%,
            100% {
              transform: scale3d(1, 1, 1);
            }
            10%,
            30% {
              transform: scale3d(1.1, 1.1, 1.1);
            }
          }
          #klarna-logo-wrapper {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #klarna-logo {
            animation: heartbeat 1.7s cubic-bezier(0.4, 0, 0.6, 1) 1s infinite;
            margin-bottom: 24px;
          }
        </style>
        <div id="klarna-logo-wrapper">
          <div id="klarna-logo">
            <svg
              width="131"
              height="54"
              viewBox="0 0 131 54"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Klarna</title>
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0.5 15.8677C0.5 7.36407 7.33232 0.46875 15.7596 0.46875H115.24C123.668 0.46875 130.5 7.36407 130.5 15.8677V38.1322C130.5 46.6358 123.668 53.53 115.24 53.53H15.7596C7.33232 53.53 0.5 46.6358 0.5 38.1322V15.8677Z"
                fill="#FFB3C7"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M100.387 22.7873C102.07 22.7873 103.632 23.3069 104.924 24.1902V23.2102H109.502V38.5148H104.924V37.5371C103.632 38.4193 102.07 38.9377 100.387 38.9377C95.9319 38.9377 92.3204 35.3236 92.3204 30.8637C92.3204 26.4037 95.9319 22.7873 100.387 22.7873ZM52.0983 22.7873C53.7814 22.7873 55.3419 23.3069 56.6355 24.1902V23.2102H61.2124V38.5148H56.6355V37.5371C55.3419 38.4193 53.7814 38.9377 52.0983 38.9377C47.643 38.9377 44.0303 35.3236 44.0303 30.8637C44.0303 26.4037 47.643 22.7873 52.0983 22.7873ZM114.362 33.0863C115.949 33.0863 117.234 34.3743 117.234 35.9625C117.234 37.5508 115.949 38.8377 114.362 38.8377C112.776 38.8377 111.489 37.5508 111.489 35.9625C111.489 34.3743 112.776 33.0863 114.362 33.0863ZM34.0622 16.3867C34.0622 21.1786 32.1963 25.6363 28.8721 28.9492L35.8839 38.5216H29.6194L21.9977 28.1193L23.9647 26.6447C27.2265 24.1982 29.0981 20.459 29.0981 16.3867H34.0622ZM21.3912 16.3867V38.5205H16.418V16.3867H21.3912ZM42.1541 16.3901V38.5148H37.3637V16.3901H42.1541ZM84.1865 22.7987C87.8741 22.7987 90.7031 25.2214 90.7031 28.7832V38.5148H86.1308V30.4407C86.1308 28.2091 84.9747 27.0051 82.9633 27.0051C81.086 27.0051 79.5267 28.1454 79.5267 30.4726V38.5148H74.9135V23.2114H79.471V24.9349C80.6283 23.3671 82.358 22.7987 84.1865 22.7987ZM68.2935 23.2102V25.2043C69.2111 24.0083 70.9203 23.2114 72.7795 23.2114V27.6645C72.7613 27.6645 72.7431 27.6622 72.725 27.6622C70.9135 27.6622 68.3037 28.9594 68.3037 31.3696V38.5148H63.6076V23.2102H68.2935ZM100.762 26.9255C98.4714 26.9255 96.6145 28.6888 96.6145 30.8637C96.6145 33.0374 98.4714 34.8007 100.762 34.8007C103.053 34.8007 104.91 33.0374 104.91 30.8637C104.91 28.6888 103.053 26.9255 100.762 26.9255ZM52.472 26.9255C50.1824 26.9255 48.3255 28.6888 48.3255 30.8637C48.3255 33.0374 50.1824 34.8007 52.472 34.8007C54.7627 34.8007 56.6196 33.0374 56.6196 30.8637C56.6196 28.6888 54.7627 26.9255 52.472 26.9255Z"
                fill="black"
              />
            </svg>
          </div>
        </div>
      `;
        this.openedWindow?.document.write(klarnaLogoHtmlAndStyle);
    }
}
exports.Popup = Popup;
